import { useState, useCallback, useRef, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router';
import toast from 'react-hot-toast';
import {
  useCreateCourseDraft,
  useUpdateCourseDraft,
  usePublishCourseDraft,
  useGetCourseDraftById,
} from '@edusense/course-client';
import { processMediaFileUrl } from '@edusense/entities-media';

import type {
  CourseEditState,
  CourseEditActions,
  ThumbnailState,
  ThumbnailActions,
  CurriculumRef,
  UseCourseEditCoreReturn
} from '../types';
import type { BasicInfoFormData } from '../types';
import type { UpdateCourseDraftBodySectionsItem, UpdateCourseDraftBodySectionsItemLecturesItem, UpdateCourseDraftBodySectionsItemLecturesItemContentType as ContentTypeEnum, UpdateCourseDraftBodySectionsItemLecturesItemPlatformSupport as PlatformSupportEnum } from '@edusense/course-client';

/**
 * 🏗️ FSD Features Layer - Course Edit Core Hook
 *
 * CourseEditPage와 CourseEditPageI18n에서 공통으로 사용하는 비즈니스 로직
 * - React Query mutations로 상태 관리
 * - 썸네일 업로드 관리
 * - 저장/발행 핸들러
 * - API 서비스 통합
 */
const isBlobOrDataUrl = (url: string): boolean => url.startsWith('blob:') || url.startsWith('data:');

const normalizeThumbnailUrl = (raw?: string | null): string => {
  if (!raw) return '';
  if (isBlobOrDataUrl(raw)) {
    return raw;
  }
  return processMediaFileUrl(raw) ?? raw;
};

const extractFileNameFromUrl = (url: string): string => {
  if (!url) return '';
  const sanitized = url.split('?')[0];
  const segments = sanitized.split('/').filter(Boolean);
  return segments.length > 0 ? segments[segments.length - 1] : '';
};

const DEFAULT_LANGUAGE = 'ko';
const FALLBACK_SUPPORTED_LANGUAGES = ['ko'];

export const useCourseEditCore = (): UseCourseEditCoreReturn => {
  const { courseId: routeCourseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // 🔍 네비게이션 디버깅: useParams로 courseId를 가져왔는지 확인
  console.log('🔍 [useCourseEditCore] Initializing', {
    routeCourseId,
    currentPath: location.pathname,
    allParams: useParams(),
  });

  // 기본 상태
  const [error, setError] = useState<string | null>(null);

  // 썸네일 상태
  const [thumbnailState, setThumbnailState] = useState<ThumbnailState>({
    currentPreviewUrl: '',
    thumbnailFileName: '',
    isUploading: false
  });
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);
  const [lastPublishedAt, setLastPublishedAt] = useState<number | null>(null);

  // Refs
  const thumbnailInputRef = useRef<HTMLInputElement>(null);
  const curriculumTabRef = useRef<CurriculumRef>(null);

  // 썸네일 관리 액션들 - multipart form 지원을 위해 File 객체 저장 추가
  const handleThumbnailChange = useCallback((event: React.ChangeEvent<HTMLInputElement>, setValue?: (name: string, value: any) => void) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setThumbnailState(prev => ({
      ...prev,
      thumbnailFileName: file.name,
      isUploading: true
    }));

    // 로컬 미리보기 URL 설정
    const previewUrl = URL.createObjectURL(file);
    setThumbnailState(prev => ({
      ...prev,
      currentPreviewUrl: previewUrl,
      isUploading: false
    }));

    // React Hook Form에 파일 객체 저장 (multipart 전송을 위해)
    if (setValue) {
      setValue('thumbnailFile', file, { shouldDirty: true, shouldTouch: true });
      setValue('thumbnailUrl', undefined, { shouldDirty: true, shouldTouch: true });
      console.log('[useCourseEditCore] 썸네일 파일 저장됨:', file.name, file.size);
    }

    toast.success('썸네일이 업로드되었습니다.');
  }, []);

  const handleRemoveThumbnail = useCallback((setValue?: (name: string, value: any) => void) => {
    setThumbnailState({
      currentPreviewUrl: '',
      thumbnailFileName: '',
      isUploading: false
    });

    if (thumbnailInputRef.current) {
      thumbnailInputRef.current.value = '';
    }

    // React Hook Form에서도 파일 제거
    if (setValue) {
      setValue('thumbnailFile', null, { shouldDirty: true, shouldTouch: true });
      setValue('thumbnailUrl', undefined, { shouldDirty: true, shouldTouch: true });
      console.log('[useCourseEditCore] 썸네일 파일 제거됨');
    }

    toast.success('썸네일이 제거되었습니다.');
  }, []);

  const triggerThumbnailUpload = useCallback(() => {
    thumbnailInputRef.current?.click();
  }, []);

  // 썸네일 프리뷰 강제 설정 (초기 데이터 로딩 및 API 응답 동기화 시 사용)
  const setThumbnailPreview = useCallback((url: string | null | undefined, fileName?: string | null) => {
    const normalizedUrl = normalizeThumbnailUrl(url);
    const normalizedFileName = normalizedUrl
      ? (fileName && fileName.trim() ? fileName : extractFileNameFromUrl(normalizedUrl))
      : '';

    setThumbnailState(prev => {
      if (
        prev.currentPreviewUrl === normalizedUrl &&
        prev.thumbnailFileName === normalizedFileName &&
        prev.isUploading === false
      ) {
        return prev;
      }

      return {
        ...prev,
        currentPreviewUrl: normalizedUrl,
        thumbnailFileName: normalizedFileName,
        isUploading: false
      };
    });
  }, []);

  // 섹션 데이터 → API 바디 매핑
  const mapSectionsForSave = (sectionsData: any[]): UpdateCourseDraftBodySectionsItem[] => {
    const toNumberOrNull = (value: any): number | null => {
      if (value === undefined || value === null || value === '') return null;
      const numeric = Number(value);
      return Number.isFinite(numeric) ? numeric : null;
    };

    const mapContentType = (rawType: any): ContentTypeEnum => {
      const normalized = typeof rawType === 'string' ? rawType.trim().toUpperCase() : '';

      switch (normalized) {
        case 'VIDEO':
          return 'VIDEO';
        case 'VIDEO_MARKDOWN':
          return 'VIDEO_MARKDOWN';
        case 'QUIZ':
          return 'QUIZ';
        case 'EXTERNAL_LINK':
          return 'EXTERNAL_LINK';
        case 'TEXT':
          return 'TEXT';
        case 'CODING':
          return 'CODING';
        default:
          // Interactive 등 정의되지 않은 값은 Markdown으로 취급
          return 'MARKDOWN';
      }
    };

    const mapPlatformSupport = (rawPlatform: any): PlatformSupportEnum => {
      const normalized = typeof rawPlatform === 'string' ? rawPlatform.trim().toUpperCase() : '';

      switch (normalized) {
        case 'IDE_ONLY':
          return 'IDE_ONLY';
        case 'IDE_AND_WEB':
          return 'IDE_AND_WEB';
        case 'WEB_ONLY':
          return 'WEB_ONLY';
        case 'IDEANDWEB':
          return 'IDE_AND_WEB';
        case 'IDEONLY':
          return 'IDE_ONLY';
        case 'WEBONLY':
          return 'WEB_ONLY';
        default:
          return 'WEB_ONLY';
      }
    };

    const normalizeLectures = (section: any): any[] => {
      if (Array.isArray(section.lectures)) return section.lectures;
      if (Array.isArray(section.lessons)) return section.lessons;
      return [];
    };

    const resolveMarkdownContent = (lesson: any): string | undefined => {
      if (typeof lesson?.localMarkdownContent === 'string') return lesson.localMarkdownContent;
      if (typeof lesson?.markdownContent === 'string') return lesson.markdownContent;
      if (typeof lesson?.markdown === 'string') return lesson.markdown;
      return undefined;
    };

    const defaultPlatform: PlatformSupportEnum = 'WEB_ONLY';

    return (sectionsData || []).map((section: any, sectionIndex: number): UpdateCourseDraftBodySectionsItem => {
      const lectures = normalizeLectures(section);

      return {
        id: toNumberOrNull(section.id) ?? undefined,
        title: section.title ?? '',
        order: toNumberOrNull(section.order) ?? sectionIndex + 1,
        lectures: lectures.map((lesson: any, lessonIndex: number): UpdateCourseDraftBodySectionsItemLecturesItem => ({
          id: toNumberOrNull(lesson.id) ?? undefined,
          title: lesson.title ?? '',
          order: toNumberOrNull(lesson.order) ?? lessonIndex + 1,
          contentType: mapContentType(lesson.type || lesson.contentType),
          platformSupport: lesson.platformSupport
            ? mapPlatformSupport(lesson.platformSupport)
            : defaultPlatform,
          duration: toNumberOrNull(lesson.duration) ?? undefined,
          videoUrl: lesson.videoUrl ?? undefined,
          markdownContent: resolveMarkdownContent(lesson),
          markdownUrl: lesson.markdownUrl ?? undefined,
        })),
      };
    });
  };

  // React Query Mutations
  const createCourseDraftMutation = useCreateCourseDraft({
    mutation: {
      onSuccess: (data) => {
        toast.success('강의 초안이 생성되었습니다.');
        console.log('[useCourseEditCore] createCourseDraft response:', data);
        setError(null);
        setLastSavedAt(Date.now());

        if (data?.thumbnailUrl) {
          setThumbnailPreview(data.thumbnailUrl);
        }

        // 새로 생성된 draft의 ID로 리다이렉션
        const newCourseId = data?.id;

        if (newCourseId) {
          console.log('[useCourseEditCore] 새 course ID로 리다이렉션:', newCourseId);

          // 현재 경로에서 언어 prefix 추출 (예: /ko/learn/course/new/edit -> /ko)
          const currentPath = location.pathname;
          const langMatch = currentPath.match(/^\/[a-z]{2}\//);
          const langPrefix = langMatch ? langMatch[0].slice(0, -1) : '';

          // React Router를 사용한 client-side navigation (언어 prefix 포함)
          const newPath = langPrefix
            ? `${langPrefix}/learn/course/${newCourseId}/edit`
            : `/courses/${newCourseId}/edit`;

          console.log('[useCourseEditCore] 리다이렉션 경로:', newPath);
          navigate(newPath, { replace: true });
        } else {
          console.error('[useCourseEditCore] ⚠️ 생성된 draft ID를 찾을 수 없습니다!');
          console.error('[useCourseEditCore] Response:', data);
        }
      },
      onError: (error) => {
        console.error('저장 중 오류:', error);
        const errorMessage = error instanceof Error ? error.message : '저장 중 오류가 발생했습니다.';
        setError(errorMessage);
        toast.error(errorMessage);
      }
    },
    request: { withCredentials: true }
  });

  const updateCourseDraftMutation = useUpdateCourseDraft({
    mutation: {
      onSuccess: (data) => {
        toast.success('전체 저장이 완료되었습니다.');
        console.log('[useCourseEditCore] updateCourseDraft response', data);
        setError(null);
        setLastSavedAt(Date.now());

        if (data?.thumbnailUrl) {
          setThumbnailPreview(data.thumbnailUrl);
        }
      },
      onError: (error) => {
        console.error('저장 중 오류:', error);
        const errorMessage = error instanceof Error ? error.message : '저장 중 오류가 발생했습니다.';
        setError(errorMessage);
        toast.error(errorMessage);
      }
    },
    request: { withCredentials: true }
  });

  const publishCourseDraftMutation = usePublishCourseDraft({
    mutation: {
      onSuccess: (data) => {
        console.log('[useCourseEditCore] publishCourseDraft response', data);
        toast.success('강좌가 성공적으로 발행되었습니다.');
        setError(null);
        setLastPublishedAt(Date.now());
      },
      onError: (error) => {
        console.error('발행 중 오류:', error);
        const errorMessage = error instanceof Error ? error.message : '발행 중 오류가 발생했습니다.';
        setError(errorMessage);
        toast.error(errorMessage);
      }
    },
    request: { withCredentials: true }
  });

  // React Query로 상세 데이터 조회
  const queryEnabled = !!routeCourseId && routeCourseId !== 'new';
  console.log('🔍 [useCourseEditCore] useGetCourseDraftById query config:', {
    routeCourseId,
    queryEnabled,
    willFetchData: queryEnabled,
  });

  const { data: courseData, isLoading: isLoadingDetails, error: loadError } = useGetCourseDraftById(
    routeCourseId || '',
    {
      query: {
        enabled: queryEnabled,
        onSuccess: (data) => {
          console.log('✅ [useCourseEditCore] Course data loaded successfully:', {
            courseId: data?.id,
            title: data?.title,
            hasSections: (data?.sections?.length ?? 0) > 0,
          });
          const remoteUrl = data?.thumbnailUrl ?? '';
          const derivedFileName = remoteUrl ? extractFileNameFromUrl(remoteUrl) : '';
          setThumbnailPreview(remoteUrl, derivedFileName);
        },
        onError: (error) => {
          console.error('❌ [useCourseEditCore] 강좌 로딩 중 오류:', error);
          const errorMessage = error instanceof Error ? error.message : '강좌 로딩 중 오류가 발생했습니다.';
          setError(errorMessage);
          toast.error(errorMessage);
        }
      },
      request: { withCredentials: true }
    }
  );

  // 전체 저장 핸들러 (폼 데이터 포함)
  const handleSaveAll = useCallback(async (form: BasicInfoFormData) => {
    console.log('[useCourseEditCore] handleSaveAll 호출됨', form);

    setError(null);

    const curriculumPayload = curriculumTabRef.current?.getSectionsDataForSave();
    const sectionsForApi = mapSectionsForSave(curriculumPayload?.sections || []);

    const normalizeLanguageCode = (value?: string | null) => {
      if (!value) {
        return null;
      }
      const trimmed = `${value}`.trim();
      if (!trimmed) {
        return null;
      }
      return trimmed.toLowerCase();
    };

    const rawSupportedLanguages = Array.isArray(form.supportedLanguages) ? form.supportedLanguages.filter(Boolean) : [];
    const originalSupportedLanguages = rawSupportedLanguages.length > 0
      ? Array.from(new Set(rawSupportedLanguages))
      : [...FALLBACK_SUPPORTED_LANGUAGES];
    if (originalSupportedLanguages.length === 0) {
      originalSupportedLanguages.push('ko');
    }

    const normalizedSupportedLanguages = originalSupportedLanguages
      .map(code => normalizeLanguageCode(code))
      .filter((code): code is string => Boolean(code));

    const normalizedDefaultLanguage = normalizeLanguageCode(form.defaultLanguage)
      ?? normalizedSupportedLanguages[0]
      ?? DEFAULT_LANGUAGE;
    const defaultLanguage = originalSupportedLanguages.find(
      lang => normalizeLanguageCode(lang) === normalizedDefaultLanguage,
    ) ?? originalSupportedLanguages[0] ?? DEFAULT_LANGUAGE;

    const activeBasicInfoLanguageNormalized = normalizeLanguageCode(form.activeBasicInfoLanguage)
      ?? normalizedDefaultLanguage;
    const activeLanguage = normalizeLanguageCode(curriculumPayload?.activeLanguage)
      ?? activeBasicInfoLanguageNormalized
      ?? normalizedDefaultLanguage;

    const sanitizedLocalizedSectionsMap: Record<string, any[]> = {};
    Object.entries(curriculumPayload?.localizedSections ?? {}).forEach(([languageCode, sections]) => {
      const normalizedLang = normalizeLanguageCode(languageCode);
      if (!normalizedLang) {
        return;
      }
      sanitizedLocalizedSectionsMap[normalizedLang] = sections || [];
    });

    const localizedSectionsForApi: Record<string, UpdateCourseDraftBodySectionsItem[]> = {};
    originalSupportedLanguages.forEach(languageCode => {
      const normalizedLang = normalizeLanguageCode(languageCode) ?? languageCode.toLowerCase();
      localizedSectionsForApi[languageCode] = mapSectionsForSave(
        sanitizedLocalizedSectionsMap[normalizedLang] || [],
      );
    });

    Object.entries(sanitizedLocalizedSectionsMap).forEach(([normalizedLang, sections]) => {
      const originalLanguageCode = originalSupportedLanguages.find(
        lang => normalizeLanguageCode(lang) === normalizedLang,
      ) ?? normalizedLang;
      if (!localizedSectionsForApi[originalLanguageCode]) {
        localizedSectionsForApi[originalLanguageCode] = mapSectionsForSave(sections || []);
      }
    });

    if (!localizedSectionsForApi[defaultLanguage]) {
      localizedSectionsForApi[defaultLanguage] = sectionsForApi;
    }

    const localizedBasicInfoForApi: Record<string, { title: string; description?: string | null }> = {};
    Object.entries(form.localizedBasicInfo ?? {}).forEach(([languageCode, entry]) => {
      localizedBasicInfoForApi[languageCode] = {
        title: entry?.title ?? '',
        description: entry?.description ?? '',
      };
    });

    originalSupportedLanguages.forEach(languageCode => {
      if (!localizedBasicInfoForApi[languageCode]) {
        localizedBasicInfoForApi[languageCode] = { title: '', description: '' };
      }
    });

    if (!localizedBasicInfoForApi[defaultLanguage]) {
      localizedBasicInfoForApi[defaultLanguage] = {
        title: form.title ?? '',
        description: form.description ?? '',
      };
    }

    const defaultBasicInfoEntry = localizedBasicInfoForApi[defaultLanguage];

    const rawPriceAmount = typeof form.priceAmount === 'number'
      ? form.priceAmount
      : Number(form.priceAmount ?? 0);
    const normalizedPriceAmount = Number.isFinite(rawPriceAmount) ? Math.max(0, rawPriceAmount) : 0;
    const normalizedPriceCurrency = (form.priceCurrency ?? 'KRW')?.toString().trim().toUpperCase() || 'KRW';
    const isFreeCourse = form.isFree ?? normalizedPriceAmount <= 0;

    const jsonData: Record<string, any> = {
      title: defaultBasicInfoEntry?.title ?? form.title ?? '',
      description: defaultBasicInfoEntry?.description ?? form.description ?? null,
      level: form.level,
      sections: sectionsForApi,
      isPublic: form.isPublic ?? true,
      allowComments: form.allowComments ?? true,
      defaultLanguage,
      supportedLanguages: originalSupportedLanguages,
      activeLanguage,
      activeBasicInfoLanguage: activeBasicInfoLanguageNormalized,
      localizedSections: localizedSectionsForApi,
      localizedBasicInfo: localizedBasicInfoForApi,
      draftVersion: form.draftVersion ?? null,
      price: isFreeCourse
        ? null
        : {
            amount: normalizedPriceAmount,
            currency: normalizedPriceCurrency,
          },
    };

    const requestBody = {
      data: JSON.stringify(jsonData),
      ...(form.thumbnailFile ? { thumbnail: form.thumbnailFile } : {}),
    };

    console.log('[useCourseEditCore] multipart payload 생성 완료');

    if (!routeCourseId || routeCourseId === 'new') {
      createCourseDraftMutation.mutate({ data: requestBody as any });
    } else {
      updateCourseDraftMutation.mutate({
        id: routeCourseId,
        data: requestBody as any
      });
    }
  }, [routeCourseId, createCourseDraftMutation, updateCourseDraftMutation]);

  // 발행 핸들러
  const handlePublish = useCallback(async () => {
    console.log('[useCourseEditCore] handlePublish 호출됨');

    if (!routeCourseId) {
      toast.error('강좌 ID가 없습니다.');
      return;
    }

    setError(null);
    publishCourseDraftMutation.mutate({ id: routeCourseId });
  }, [routeCourseId, publishCourseDraftMutation]);

  // 상세 로드 (React Query 데이터 반환)
  const loadCourseDetails = useCallback(async (): Promise<any | null> => {
    if (!routeCourseId || routeCourseId === 'new') return null;
    // React Query의 현재 캐시된 데이터 반환
    return courseData || null;
  }, [routeCourseId, courseData]);

  // 통합 loading 상태
  const isSaving = createCourseDraftMutation.isLoading || updateCourseDraftMutation.isLoading;
  const isPublishing = publishCourseDraftMutation.isLoading;

  const loading = isSaving || isPublishing || isLoadingDetails;

  // 상태와 액션 객체 구성
  const state: CourseEditState = {
    loading,
    isSaving,
    isPublishing,
    lastSavedAt,
    lastPublishedAt,
    error,
    thumbnailState
  };

  const thumbnailActions: ThumbnailActions = {
    handleThumbnailChange,
    handleRemoveThumbnail,
    triggerThumbnailUpload
  };

  const actions: CourseEditActions = {
    thumbnailActions,
    handleSaveAll,
    handlePublish,
    setThumbnailPreview,
    loadCourseDetails
  };

  return {
    state,
    actions,
    courseId: routeCourseId,
    thumbnailInputRef,
    curriculumTabRef,
    courseData  // React Query에서 가져온 실시간 데이터 노출
  };
};
